import { useEffect, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Table from "../../components/common/Table/Table";
import { userTableColumns } from "../../config/tableColumns";
import api from "../../api/ApiService";
import { formatDate } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import {
  yojanaDropdownOptions,
  yojanaOptions,
} from "../../config/appliedSchemesConfig";
import DownloadExcel from "../../components/common/DownloadExcel";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedScheme, setSelectedScheme] = useState("");
  const [selectedConstructionStatus, setSelectedConstructionStatus] =
    useState("");
  const navigate = useNavigate();

  const constructionStatusList = [
    { label: "Completed", value: "Completed" },
    {
      label: "Under Construction",
      value: "Under Construction",
    },
    {
      label: "Construction not started",
      value: "Construction not started",
    },
  ];

  const fetchUsers = async (page = 1, perPage = 10, search = "") => {
    try {

      const queryParams = new URLSearchParams({
        perPage,
        page,
        search,
      });

      if (selectedScheme) {
        queryParams.append("scheme", selectedScheme);
      }

      if (selectedConstructionStatus) {
        queryParams.append("constructionStatus", selectedConstructionStatus);
      }

      const response = await api.get(`/admin/invest-uk/users?${queryParams.toString()}`);

      if (response.data.data) {
        const formatted = response.data.data.map((item, index) => {
          const caf = item.caf_details[0]; // safely access first CAF detail
          let totalInvestment = "N/A";
          let constructionStatus = "N/A";
          let appliedScheme = "N/A";
          let expectedCompletionDate = "N/A";
          let cafApprovalDate = caf?.automatic_data?.updated_date
            ? formatDate(caf.automatic_data.updated_date)
            : "N/A";
          let land = "N/A";
          let buildingPlanApproval = "N/A";
          let is143Approved = "N/A";

          if (caf?.manual_data?.details) {
            try {
              const details = JSON.parse(caf.manual_data.details);
              totalInvestment = details?.total_investment
                ? details.total_investment + " Cr"
                : "N/A";
              constructionStatus = details?.construction_status
                ? details.construction_status
                : "N/A";
              expectedCompletionDate = details?.expected_date_of_completion
                ? formatDate(details.expected_date_of_completion)
                : "N/A";

              appliedScheme =
                yojanaOptions.find(
                  (y) => y.id === details?.available_incentive_scheme,
                )?.label ?? "N/A";
              land = details?.land_available ? details.land_available : "No";
              buildingPlanApproval = details?.building_plan_approval
                ? details.building_plan_approval
                : "No";
              is143Approved = details?.section_143_approval
                ? details.section_143_approval
                : "No";
            } catch (err) {
              console.error("Invalid JSON in manual_data.details", err);
            }
          }

          return {
            sr_no: (page - 1) * perPage + index + 1,
            name: item.name,
            user_id: item.id,
            mobile_number: item.mobile_number,
            caf_id: caf?.caf_id || "N/A",
            applied_scheme:
              appliedScheme || "Uttarakhand Paryatan Udhyami Protsahan Yojana",
            unit_name: caf?.automatic_data?.entity_name || "N/A",
            district: caf?.automatic_data?.district_name || "N/A",
            construction_status: constructionStatus,
            investment: totalInvestment,
            expected_completion_date: expectedCompletionDate,
            caf_approval_date: cafApprovalDate,
            applied_for_claim: item.applied_for_claim || "No",
            land: land,
            one_four_three_approved: is143Approved,
            building_plan_approval: buildingPlanApproval,
            dtdo_remarks: item.dtdo_remarks || "N/A",
          };
        });

        setUsers(formatted);
        setTotalEntries(response.data.total);
      } else {
        setUsers([]);
        setTotalEntries(0);
      }
    } catch (error) {
      setUsers([]);
      setTotalEntries(0);
      console.error("Failed to fetch data", error);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, rowsPerPage, searchQuery);
  }, [currentPage, rowsPerPage, searchQuery, selectedScheme, selectedConstructionStatus]);

  const handleView = (row) => {
    navigate(`/admin/user-details`, { state: { data: row } });
  };

  return (
    <DashboardLayout pageTitle="Registered Users" mainClass="p-6">
      <div className="flex justify-end h-10 mb-4">
        <select
          value={selectedScheme}
          onChange={(e) => {
            setSelectedScheme(e.target.value);
            setCurrentPage(1); // reset pagination
          }}
          className="border rounded px-2 py-1 text-sm mr-4"
        >
          <option value="">All Schemes</option>
          {yojanaDropdownOptions.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>

        <select
          value={selectedConstructionStatus}
          onChange={(e) => {
            setSelectedConstructionStatus(e.target.value);
            setCurrentPage(1); // reset pagination
          }}
          className="border rounded px-2 py-1 text-sm mr-4"
        >
          <option value="">All type of Construction</option>
          {constructionStatusList.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <DownloadExcel
          apiEndpoint="/admin/invest-uk/users/excel"
          queryParams={{
            search: searchQuery,
            district_name: "",
            scheme: selectedScheme,
            constructionStatus: selectedConstructionStatus,
          }}
        />
      </div>
      <div className="bg-white p-6 rounded shadow-md  mx-auto">
        <div className="flex h-12 nav-bg p-3 text-white font-semibold rounded-tr rounded-tl">
          User List
        </div>
        <Table
          columns={userTableColumns(handleView)}
          data={users}
          totalEntries={totalEntries}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          rowsPerPageOptions={[5, 10, 20, 30, 100]}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminUsers;
